import { BodyText, Gutter, Input, Title } from '@gaiads/telia-react-component-library';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CopyableText, PopupModal } from '@cdaas/reusable-components-daas';
import { CountryAcquisitionPrice, AdminProductVariant, Country } from '@cdaas/api-clients/dist/product-service-openapi';

import { upsertEntity } from '../../../utils/entities';

import './PricingEditModal.scss';

interface PricingEditModalProps {
  open: boolean;
  productName?: string;
  variants: AdminProductVariant[];
  countryId: number;
  countries: Country[];
  onAction: (variantsWithChangedAcquisitionPrices: AdminProductVariant[]) => void;
  onClose: () => void;
}

const PricingEditModal = ({ open, productName, variants, countryId, countries, onAction, onClose }: PricingEditModalProps) => {
  const { t } = useTranslation('productscreen');
  const [amount, setAmount] = useState<number>();

  const country = countries.find(country => country.id === countryId);
  const currency = country?.currency || '';

  useEffect(() => {
    // For now each variant should have the same acquisition price in same country.
    // If any variant doesn't have an acquisition price in the currently chosen country => interpret it as missing price.
    const acquisitionPricesInSelectedCountry = variants.reduce((acquisitionPrices: CountryAcquisitionPrice[], variant) => {
      const acquisitionPrice = variant.country_acquisition_prices.find(price => price.country_id === countryId);
      if (acquisitionPrice) {
        acquisitionPrices.push(acquisitionPrice);
      }
      return acquisitionPrices;
    }, []);
    const acquisitionPrice = acquisitionPricesInSelectedCountry?.[0];
    setAmount(acquisitionPrice?.amount);
  }, [countryId, variants]);

  const handleChangeAcquisitionPriceForAllVariantsInCurrentCountry = () => {
    if (!amount) return;
    const variantsWithChangedAcquisitionPriceInCurrentCountry = variants.map(variant => {
      let varintAcquisitionPriceForCurrentCountry = variant.country_acquisition_prices.find(price => price.country_id === countryId);
      if (varintAcquisitionPriceForCurrentCountry) {
        varintAcquisitionPriceForCurrentCountry.amount = amount;
      } else {
        varintAcquisitionPriceForCurrentCountry = {
          amount: amount,
          country_id: countryId,
          variant_id: variant.id,
          id: 0,
        };
      }
      const updatedAcquisitionPricesForVariant = upsertEntity(variant.country_acquisition_prices, varintAcquisitionPriceForCurrentCountry);
      return {
        ...variant,
        country_acquisition_prices: updatedAcquisitionPricesForVariant,
      };
    });
    onAction(variantsWithChangedAcquisitionPriceInCurrentCountry);
  };

  return (
    <PopupModal
      translations={{
        heading: t('pricingEditModal.heading'),
        actionButton: t('pricingEditModal.saveChanges'),
        closeButton: t('pricingEditModal.cancel'),
      }}
      onAction={handleChangeAcquisitionPriceForAllVariantsInCurrentCountry}
      onClose={onClose}
      actionButtonDisabled={!amount}
      hidden={!open}
    >
      <>
        <Title size="xxs" margin={{ bottom: 'xs' }}>
          {t('pricingEditModal.productTitle')}
        </Title>
        <CopyableText
          text={productName || ''}
          translations={{ copyLinkLabel: t('pricingEditModal.copy'), copyMessage: t('pricingEditModal.copyMessage') }}
          textProps={{ size: 'md' }}
        />
        <Gutter />
        <Input
          label={t('pricingEditModal.financeAcquisitionPriceInputLabel', { currency })}
          aria-label={t('pricingEditModal.financeAcquisitionPriceInputLabel', { currency })}
          // @ts-expect-error Type 'number' is not assignable to type 'search' | 'text' | 'tel' | 'url' | 'email' | 'password' | undefined
          type={'number'}
          min={0}
          step={'.01'}
          value={amount?.toString() || ''}
          onChange={(_event, value) => setAmount(Number(value))}
          className="pricing-edit-modal__finance-acquisition-price-input"
          errorText={!amount ? t('pricingEditModal.missingFinanceAcquisitionPriceError') : undefined}
        />
        <Gutter />
        <BodyText size="sm">{t('pricingEditModal.financeMonthlyPriceLabel', { currency })}</BodyText>
        <Gutter size="sm" />
        <BodyText size="md" padding={{ left: 'md' }} color="gray800">
          {t('pricingEditModal.financeMonthlyPriceValueInfo')}
        </BodyText>
      </>
    </PopupModal>
  );
};

export default PricingEditModal;
